import React, { useState } from 'react';
import { ActivityType, ACTIVITY_CONFIGS } from '../../types/activity-types';
import ItinerariesSvg from '../itineraries/itineraries-svg';
import { X } from 'lucide-react';

interface MapLegendConfigProps {
  onClose: () => void;
  position: { x: number; y: number };
}

interface TypeConfig {
  color: string;
  iconType: ActivityType;
}

const DEFAULT_COLORS = [
  '#842234', // Maroon (default)
  '#FF6B6B',
  '#4169E1',
  '#10B981',
  '#F59E0B',
  '#A855F7',
  '#EC4899',
  '#14B8A6',
  '#EF4444',
  '#8B5CF6',
  '#06B6D4',
  '#84CC16',
  '#F97316',
  '#6366F1',
  '#D946EF' // Fuchsia
];

const MapLegendConfig: React.FC<MapLegendConfigProps> = ({
  onClose,
  position
}) => {
  // Load saved configurations from sessionStorage or use defaults
  const loadConfigs = (): Record<ActivityType, TypeConfig> => {
    const saved = sessionStorage.getItem('mapLegendConfigs');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // Fall through to defaults
      }
    }

    // Default configurations
    const configs: Record<ActivityType, TypeConfig> = {} as Record<
      ActivityType,
      TypeConfig
    >;
    Object.values(ActivityType).forEach((type) => {
      configs[type] = {
        color: ACTIVITY_CONFIGS[type].color,
        iconType: type
      };
    });
    return configs;
  };

  const [configs, setConfigs] =
    useState<Record<ActivityType, TypeConfig>>(loadConfigs());
  const [expandedType, setExpandedType] = useState<ActivityType | null>(null);

  const handleColorChange = (type: ActivityType, color: string) => {
    const newConfigs = {
      ...configs,
      [type]: { ...configs[type], color }
    };
    setConfigs(newConfigs);
    sessionStorage.setItem('mapLegendConfigs', JSON.stringify(newConfigs));
  };

  const handleIconChange = (type: ActivityType, iconType: ActivityType) => {
    const newConfigs = {
      ...configs,
      [type]: { ...configs[type], iconType }
    };
    setConfigs(newConfigs);
    sessionStorage.setItem('mapLegendConfigs', JSON.stringify(newConfigs));
  };

  const handleReset = () => {
    const defaultConfigs: Record<ActivityType, TypeConfig> = {} as Record<
      ActivityType,
      TypeConfig
    >;
    Object.values(ActivityType).forEach((type) => {
      defaultConfigs[type] = {
        color: ACTIVITY_CONFIGS[type].color,
        iconType: type
      };
    });
    setConfigs(defaultConfigs);
    sessionStorage.setItem('mapLegendConfigs', JSON.stringify(defaultConfigs));
  };

  // Calculate position to keep dialog on screen — clamped to viewport bounds
  const dialogPosition: React.CSSProperties = {
    left: Math.min(position.x, Math.max(0, window.innerWidth - 420)),
    top: Math.min(position.y, Math.max(0, window.innerHeight - 580))
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 2999
        }}
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        className='map-legend-dialog'
        style={dialogPosition}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f9fafb',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px'
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 600,
              color: '#111827'
            }}
          >
            Map Legend & Configuration
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              color: '#6b7280'
            }}
            title='Close'
          >
            <X size={20} />
          </button>
        </div>

        {/* Notification */}
        <div
          style={{
            padding: '12px 20px',
            backgroundColor: '#FEF3C7',
            borderBottom: '1px solid #F59E0B',
            display: 'flex',
            alignItems: 'start',
            gap: '8px'
          }}
        >
          <div style={{ fontSize: '16px', marginTop: '2px' }}>ℹ️</div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#92400E',
                marginBottom: '4px'
              }}
            >
              Temporary Type Customizations
            </div>
            <div
              style={{ fontSize: '12px', color: '#78350F', lineHeight: '1.4' }}
            >
              Changes to activity type colors and icons are temporary and will
              reset when you refresh the page. To save permanent customizations,
              edit individual activities.
            </div>
          </div>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px 20px'
          }}
        >
          {Object.values(ActivityType).map((type) => {
            const config = configs[type];
            const activityConfig = ACTIVITY_CONFIGS[type];
            const isExpanded = expandedType === type;

            return (
              <div
                key={type}
                style={{
                  marginBottom: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  overflow: 'hidden'
                }}
              >
                {/* Type Header */}
                <div
                  style={{
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: isExpanded ? '#f9fafb' : 'white',
                    cursor: 'pointer'
                  }}
                  onClick={() => setExpandedType(isExpanded ? null : type)}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}
                  >
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '4px'
                      }}
                    >
                      <ItinerariesSvg
                        type={config.iconType}
                        fill={config.color}
                        style={{ width: '20px', height: '20px' }}
                      />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#111827'
                        }}
                      >
                        {activityConfig.displayName}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {activityConfig.category}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      backgroundColor: config.color,
                      border: '2px solid white',
                      boxShadow: '0 0 0 1px #e5e7eb'
                    }}
                  />
                </div>

                {/* Expanded Configuration */}
                {isExpanded && (
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: '#fafafa',
                      borderTop: '1px solid #e5e7eb'
                    }}
                  >
                    {/* Color Selection */}
                    <div style={{ marginBottom: '12px' }}>
                      <label
                        style={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#374151',
                          display: 'block',
                          marginBottom: '8px'
                        }}
                      >
                        Pin Color
                      </label>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px'
                        }}
                      >
                        {DEFAULT_COLORS.map((color, index) => (
                          <button
                            key={`${color}-${index}`}
                            onClick={() => handleColorChange(type, color)}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '4px',
                              backgroundColor: color,
                              border:
                                config.color === color
                                  ? '2px solid #111827'
                                  : '2px solid white',
                              boxShadow: '0 0 0 1px #e5e7eb',
                              cursor: 'pointer',
                              padding: 0
                            }}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Icon Type Selection */}
                    <div>
                      <label
                        style={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#374151',
                          display: 'block',
                          marginBottom: '8px'
                        }}
                      >
                        Icon Type
                      </label>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px'
                        }}
                      >
                        {Object.values(ActivityType).map((iconType) => (
                          <button
                            key={iconType}
                            onClick={() => handleIconChange(type, iconType)}
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '4px',
                              backgroundColor:
                                config.iconType === iconType
                                  ? '#e5e7eb'
                                  : 'white',
                              border:
                                config.iconType === iconType
                                  ? '2px solid #842234'
                                  : '1px solid #e5e7eb',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0
                            }}
                            title={ACTIVITY_CONFIGS[iconType].displayName}
                          >
                            <ItinerariesSvg
                              type={iconType}
                              fill='#2D2D2D'
                              style={{ width: '20px', height: '20px' }}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            backgroundColor: '#f9fafb',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px'
          }}
        >
          <button
            onClick={handleReset}
            style={{
              padding: '8px 16px',
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              color: '#374151'
            }}
          >
            Reset to Defaults
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#842234',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              color: 'white'
            }}
          >
            Done
          </button>
        </div>
      </div>
    </>
  );
};

export default MapLegendConfig;
